import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { backTargetFromLocation, withBackTarget } from "@/app/back-target";
import { caseActionPath } from "@/app/routes";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { isInteractiveTarget } from "@/lib/dom/is-interactive-target";
import { formatDate } from "@/lib/format/datetime";

import { usePendingApprovals } from "./use-approvals";
import { useBulkApprove } from "./use-bulk-approve";

/**
 * 大項目3「承認リクエスト」。いま自分（または所属グループ）に回っている承認待ち。
 *
 * 単件は判定画面（/approval-requests/:caseId/:taskGuid）へ遷移して内容を確認して
 * から判定する（一覧からのワンクリック承認はしない）。判定にはプロセス全体と
 * 申請内容が要るので、サイドパネルではなくページにしている。
 * 一括承認は「内容を確認済みのものをまとめて処理する」補助手段として別に用意し、
 * 判定項目が無いタスクは送らずスキップする。
 *
 * 1 件判定すると一覧の並び順のまま次の案件を続けて開く（連続承認）。最後まで
 * 到達したら閉じる。途中でやめたいときはドロワーを閉じる。
 *
 * 承認済み履歴タブは作らない（横断で「自分が承認した履歴」を取る API が無い。
 * 全案件モニタ → 案件 → 履歴タブで追う）。
 */
export function ApprovalListPage() {
  const { t } = useTranslation(["common", "domain"]);
  const { data, isPending, isError } = usePendingApprovals();
  const navigate = useNavigate();
  // 詳細から戻ったときに承認リクエストへ返す（既定はマイ案件なので明示が要る）
  const backTo = backTargetFromLocation(useLocation());
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const bulk = useBulkApprove();

  const items = data ?? [];
  const allSelected = items.length > 0 && selected.size === items.length;

  const toggle = (taskGuid: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(taskGuid)) next.delete(taskGuid);
      else next.add(taskGuid);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(
      allSelected ? new Set() : new Set(items.map((item) => item.taskGuid)),
    );
  };

  const approveSelected = () => {
    const targets = items
      .filter((item) => selected.has(item.taskGuid))
      .map((item) => ({ caseId: item.caseId, taskGuid: item.taskGuid }));

    bulk.mutate(targets, { onSuccess: () => setSelected(new Set()) });
  };

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold">{t("domain:approvalRequest")}</h1>
        {data ? (
          <p className="text-muted-foreground text-sm">
            {t("common:pagination.total", { count: items.length })}
          </p>
        ) : null}
      </header>

      {isPending ? <Skeleton className="h-32 w-full" /> : null}

      {isError ? (
        <p className="text-destructive text-sm">{t("common:state.error")}</p>
      ) : null}

      {bulk.data ? <BulkResult result={bulk.data} /> : null}

      {data ? (
        items.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("common:approvals.empty")}
          </p>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                disabled={selected.size === 0 || bulk.isPending}
                onClick={approveSelected}
              >
                {t("common:approvals.bulkApprove")}
              </Button>
              {selected.size > 0 ? (
                <span className="text-muted-foreground text-sm tabular-nums">
                  {t("common:pagination.total", { count: selected.size })}
                </span>
              ) : null}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-muted/50 text-muted-foreground text-left text-xs">
                    <th scope="col" className="px-3 py-2 font-medium">
                      <input
                        type="checkbox"
                        aria-label={t("common:approvals.selectAll")}
                        checked={allSelected}
                        onChange={toggleAll}
                      />
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("domain:caseNumber")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("domain:applicationType")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("domain:applicant")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("domain:currentStep")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("common:approvals.requestedAt")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.taskGuid}
                      // 行全体で内容を開く。チェックボックスと「内容を確認」の
                      // クリックは行の処理に流さない（選択や遷移の二重反応を防ぐ）
                      onClick={(event) => {
                        if (isInteractiveTarget(event.target)) return;
                        void navigate(
                          withBackTarget(
                            caseActionPath(item.caseId, item.processModelId),
                            backTo,
                          ),
                        );
                      }}
                      className="hover:bg-muted/40 border-border cursor-pointer border-b last:border-0"
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          aria-label={item.caseNumber}
                          checked={selected.has(item.taskGuid)}
                          onChange={() => toggle(item.taskGuid)}
                        />
                      </td>
                      <td className="text-muted-foreground px-3 py-2 tabular-nums">
                        {item.caseNumber}
                      </td>
                      <td className="px-3 py-2">
                        {item.processModelDisplayName}
                      </td>
                      <td className="px-3 py-2">{item.applicant ?? "—"}</td>
                      <td className="px-3 py-2">{item.stepLabel ?? "—"}</td>
                      <td className="text-muted-foreground px-3 py-2 tabular-nums">
                        {formatDate(item.requestedAt)}
                      </td>
                      <td className="px-3 py-2">
                        {/* 開くだけのボタン。承認は判定画面と一括承認が担うので、
                            見た目は控えめにして主操作と競合させない */}
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            void navigate(
                              withBackTarget(
                                caseActionPath(
                                  item.caseId,
                                  item.processModelId,
                                ),
                                backTo,
                              ),
                            )
                          }
                        >
                          {t("common:approvals.review")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      ) : null}
    </section>
  );
}

function BulkResult({
  result,
}: {
  readonly result: {
    readonly approved: number;
    readonly skipped: readonly number[];
    readonly failed: readonly number[];
  };
}) {
  const { t } = useTranslation("common");

  return (
    <div className="flex flex-col gap-1 text-sm">
      <p className="font-medium">
        {t("approvals.bulkResult", { approved: String(result.approved) })}
      </p>
      {result.skipped.length > 0 ? (
        <p className="text-muted-foreground">
          {t("approvals.bulkSkipped", { count: result.skipped.length })}
        </p>
      ) : null}
      {result.failed.length > 0 ? (
        <p className="text-destructive">
          {t("approvals.bulkFailed", { count: result.failed.length })}
        </p>
      ) : null}
    </div>
  );
}
